import React, { useState, useCallback } from 'react';
import NavBar from './components/common/NavBar';
import HomePage from './pages/HomePage';
import ChatInterface from './components/chat/ChatInterface';
import AdminLogin from './components/admin/AdminLogin';
import OrderConfirmation from './components/books/OrderConfirmation';
import { mockBooks } from './data/mockBooks';
import { generateChatResponse } from './utils/geminiChat';

function App() {
  // View state
  const [currentView, setCurrentView] = useState('home');
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Book and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBook, setSelectedBook] = useState(null);
  const [favorites, setFavorites] = useState([]);

  // Search and filter books
  const searchBooks = useCallback((query) => {
    return mockBooks
      .filter(book => 
        (selectedCategory === 'All' || book.category === selectedCategory) &&
        (query === '' || 
          book.title.toLowerCase().includes(query.toLowerCase()) ||
          book.author.toLowerCase().includes(query.toLowerCase()) ||
          book.category.toLowerCase().includes(query.toLowerCase())
        )
      )
      .sort((a, b) => b.rating - a.rating);
  }, [selectedCategory]);
  
  // Order state
  const [orders, setOrders] = useState([]);
  const [orderConfirmation, setOrderConfirmation] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    address: ''
  });

  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');

  // Chat handlers
  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;

    const currentInput = chatInput;
    setChatInput(''); // Clear input immediately

    console.log('Processing chat input:', currentInput);

    // Add user message
    const userMessage = {
      id: Date.now(),
      text: currentInput,
      sender: 'user'
    };
    
    setMessages(prev => [...prev, userMessage]);

    // Verify environment variable
    console.log('API Key present:', !!process.env.REACT_APP_GEMINI_API_KEY);

    try {
      // Show typing indicator
      setMessages(prev => [...prev, { id: 'typing', sender: 'assistant', text: '...' }]);

      // Get AI response
      const response = await generateChatResponse(currentInput, mockBooks);
      const aiResponse = response.success ? response.message : response.error;
      
      // Remove typing indicator and add AI response
      setMessages(prev => 
        prev.filter(msg => msg.id !== 'typing')
           .concat({
              id: Date.now(),
              text: aiResponse,
              sender: 'assistant'
            })
      );
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => 
        prev.filter(msg => msg.id !== 'typing')
           .concat({
              id: Date.now(),
              text: "I apologize, but I'm having trouble processing your request at the moment. Please try again.",
              sender: 'assistant'
            })
      );
    }
  };

  // Order handlers
  const handleOrderSubmit = (order) => {
    const newOrder = {
      ...order,
      book: selectedBook,
      quantity,
      userInfo,
      timestamp: new Date().toISOString()
    };
    setOrders([...orders, newOrder]);
    setOrderConfirmation(newOrder);
    setCurrentView('confirmation');
  };

  const handleAdminLogin = (credentials) => {
    if (credentials.email === 'admin@bookify.com' && credentials.password === 'admin123') {
      setIsAdmin(true);
    }
  };

  const toggleFavorite = (bookId) => {
    setFavorites(prev => 
      prev.includes(bookId) 
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar 
        currentView={currentView} 
        setCurrentView={setCurrentView}
        isAdmin={isAdmin}
      />
      
      {currentView === 'home' && (
        <HomePage 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          searchBooks={searchBooks}
          selectedBook={selectedBook}
          setSelectedBook={setSelectedBook}
          favorites={favorites}
          toggleFavorite={toggleFavorite}
          quantity={quantity}
          setQuantity={setQuantity}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          handleOrderSubmit={handleOrderSubmit}
          mockBooks={mockBooks}
        />
      )}
      
      {currentView === 'chat' && (
        <ChatInterface 
          messages={messages}
          chatInput={chatInput}
          setChatInput={setChatInput}
          handleChatSubmit={handleChatSubmit}
        />
      )}
      
      {currentView === 'admin-login' && !isAdmin && (
        <AdminLogin onLogin={handleAdminLogin} />
      )}
      
      {currentView === 'confirmation' && orderConfirmation && (
        <OrderConfirmation order={orderConfirmation} />
      )}
    </div>
  );
}

export default App;
